import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  ToggleButtonGroup, ToggleButton, TextField, Alert, CircularProgress
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { submitFeedback, type FeedbackCategory } from "@/api/feedback";
import { getErrorMessage } from "@/utils/getErrorMessage";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  Bug: "Bug",
  FeatureRequest: "Feature request",
  General: "General"
};

/** A single lightweight dialog reachable from anywhere in the app via the
 * header icon - deliberately not a dedicated page/route, since the product
 * genuinely doesn't need another screen for this (see docs/CUSTOMER_FEEDBACK_LOOP.md). */
export default function FeedbackDialog({ open, onClose }: Props) {
  const [category, setCategory] = useState<FeedbackCategory>("General");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: () => submitFeedback({ category, message: message.trim(), page: window.location.pathname })
  });

  function handleClose() {
    onClose();
    // Reset after the close animation rather than immediately, so the
    // success message doesn't visibly flicker away before the dialog shuts.
    setTimeout(() => {
      mutation.reset();
      setMessage("");
      setCategory("General");
    }, 200);
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send feedback</DialogTitle>
      <DialogContent>
        {mutation.isSuccess ? (
          <Alert severity="success" sx={{ mt: 1 }}>Thanks - your feedback has been sent to the team.</Alert>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={category}
              onChange={(_, value) => value && setCategory(value)}
            >
              {(Object.keys(CATEGORY_LABELS) as FeedbackCategory[]).map((key) => (
                <ToggleButton key={key} value={key}>{CATEGORY_LABELS[key]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              label="What's on your mind?"
              multiline
              minRows={4}
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                category === "Bug"
                  ? "What happened, and what did you expect instead?"
                  : category === "FeatureRequest"
                    ? "What would you like AssetOps to do?"
                    : "Anything you'd like us to know?"
              }
            />
            {mutation.isError && <Alert severity="error">{getErrorMessage(mutation.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{mutation.isSuccess ? "Close" : "Cancel"}</Button>
        {!mutation.isSuccess && (
          <Button
            variant="contained"
            disabled={!message.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : "Send"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
